import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { LoggingService } from '@logging/logging.service';
import { FeatureFlagKey, FeatureFlagService, FeatureFlagsServiceInjectionToken } from '@feature-flags/feature-flag.service';

export interface OrganisationScopeRouteData {
  organisationScopeFeatureFlag: FeatureFlagKey;
}

export const getOrganisationScopeFeatureFlag = (organisationScopeFeatureFlag: FeatureFlagKey): OrganisationScopeRouteData => ({
  organisationScopeFeatureFlag
});

/**
 * Organisation-scoped bulk import is behind a per-import-type rollout flag, so a direct URL can't skip
 * employer and payroll selection while that type's flag is off. The route's `bulkImport` flag is checked
 * separately by {@link FeatureFlagEnabledRouteGuard}.
 */
@Injectable({ providedIn: 'root' })
export class BulkImportOrganisationModeRouteGuard {
  constructor(
    private logger: LoggingService,
    @Inject(FeatureFlagsServiceInjectionToken) private featureFlagService: FeatureFlagService
  ) {}

  /**
   * The import type's flag should be passed in as {@link https://angular.io/api/router/Data|Data} config,
   * with {@link getOrganisationScopeFeatureFlag}, otherwise an error will be thrown.
   */
  canActivate(next: ActivatedRouteSnapshot): Observable<boolean> {
    const { organisationScopeFeatureFlag } = next.data as OrganisationScopeRouteData;
    if (!organisationScopeFeatureFlag)
      throw new Error(
        `Route Guard activated without specifying the import type's payroll-less feature flag. Add the 'getOrganisationScopeFeatureFlag' to the route's config.`
      );

    this.logger.trace('BulkImportOrganisationModeRouteGuard: checking flag', { organisationScopeFeatureFlag });
    return this.featureFlagService.getValue$<boolean>(organisationScopeFeatureFlag, false).pipe(take(1));
  }
}
